import { Checkbox } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '../../hooks/useBoolean';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { DataItem, updateDailyTool } from '../../services/apiSLTP/activities';
import { createDailyTool, DailyTool, deleteDailyTool } from '../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getPriceTypeInfos, materialState, stepModes, systemConfigKeyEnum } from '../../utils/enums';
import DraggableCell from '../DraggableCell';
import DroppableCell from '../DroppableCell';
import ManageDailyToolModal from '../modals/operations/planning/ManageDailyToolModal';
import IconButton from '../shared/IconButton';

interface ToolsCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
    createTransfer?: boolean;
}

const ToolsCell: React.FC<ToolsCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
    createTransfer = true,
}) => {
    const itemType = 'TOOLS';
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const manageModal = useBoolean(false);
    const [tool, setTool] = useState<DailyTool | null>(null);

    const countDuplicateTool = (toolId: string) => {
        const matchingToolIds = activities.flatMap((activity) => activity.dailyTools).filter((dailyTool) => dailyTool.toolId._id === toolId);
        if (matchingToolIds.length === 1) return 1;
        if (
            matchingToolIds.length === 2 &&
            (matchingToolIds[0].state === materialState.NEW || matchingToolIds[1].state === materialState.NEW) &&
            (matchingToolIds[0].state === materialState.REMOVE || matchingToolIds[1].state === materialState.REMOVE)
        )
            return 1;
        return matchingToolIds.length;
    };

    const handleToolsChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            await updateDailyTool(fromItemId, { activityId: toLineId, createTransfer: true }, { deleteTransfers: true });
            refetch();
        } catch (error) {
            console.error('Error updating daily tool:', error);
        }
    };

    const handleDeleteTool = async (id: string, index: number) => {
        try {
            await deleteDailyTool(id, { deleteTransfers: true });
            refetch();
        } catch (error) {
            console.error('Error deleting daily tool:', error);
        }
    };

    const handleCreateDailyTool = async (dailyToolId: string) => {
        const selectedTool = activity.dailyTools.find((value) => value._id === dailyToolId);
        if (selectedTool) {
            const response = await createDailyTool(activity._id, selectedTool.toolId._id, selectedTool.state, createTransfer);
            refetch();
        }
    };

    return (
        <div className={`${'text-black'}`} style={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1 }}>
            <DroppableCell
                dropId={{ lineId: activity._id }}
                moveCell={(fromLineId, fromItemId, toLineId) => handleToolsChange(fromLineId, fromItemId, toLineId)}
                itemType={itemType}
                disabled={disabled}
            >
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => {
                                    setTool(null);
                                    manageModal.setTrue();
                                }}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addTool'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {activity.dailyTools.map((tool, index) => (
                            <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                <DraggableCell
                                    dragIds={{ itemId: tool._id, lineId: activity._id }}
                                    index={index}
                                    itemType={itemType}
                                    onDelete={() => handleDeleteTool(tool._id, index)}
                                    disabled={disabled}
                                    state={tool.state}
                                    warning={countDuplicateTool(tool.toolId._id) > 1}
                                    warningChar={countDuplicateTool(tool.toolId._id).toString()}
                                    onDuplicate={() => handleCreateDailyTool(tool._id)}
                                    onDoubleClick={() => {
                                        setTool(tool);
                                        manageModal.setTrue();
                                    }}
                                >
                                    <div className="one-line-container">
                                        <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                            <div className="one-line" title={tool.toolId.designation}>
                                                {tool.toolId.designation}
                                            </div>
                                        </div>
                                        {activity.stepMode === stepModes.VALIDATE && (
                                            <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                                <div className="one-line" title={tool.toolId.codeProC}>
                                                    {tool.toolId.codeProC}
                                                </div>
                                            </div>
                                        )}
                                        {activity.stepMode === stepModes.VALIDATE && (
                                            <div
                                                className="one-line font-italic"
                                                style={{
                                                    color: '#DC4D23',
                                                    textDecorationLine: tool?.isUsed && activity.includedInExpenses ? undefined : 'line-through',
                                                }}
                                            >
                                                {tool.price ?? tool.toolId.price ?? tool.toolId.toolTypeId.price}{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}/
                                                {capitalizeFirstLetter(
                                                    t(getPriceTypeInfos(tool.priceType ?? tool.toolId.priceType ?? tool.toolId.toolTypeId.priceType))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </DraggableCell>
                                {(activity.stepMode === stepModes.REPORT || activity.stepMode === stepModes.VALIDATE) && (
                                    <div className="h-25px fx-r fx-items-c">
                                        <Checkbox
                                            checked={tool.isUsed}
                                            onChange={async (_, checked) => {
                                                await updateDailyTool(tool._id, { isUsed: checked });
                                                refetch();
                                            }}
                                            color="success"
                                            sx={{ padding: 0 }}
                                            disabled={disabled || !activity.includedInExpenses}
                                            title={capitalizeFirstLetter(t('isUsed'))}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DroppableCell>
            {manageModal.value && (
                <ManageDailyToolModal
                    open={manageModal.value}
                    activityId={activity._id}
                    handleClose={() => manageModal.setFalse()}
                    refetch={refetch}
                    dailyTool={tool}
                    createTransfer={createTransfer}
                    activities={activities}
                    tool={null}
                    stepMode={activity.stepMode}
                />
            )}
        </div>
    );
};

export default ToolsCell;
